import JSZip from 'jszip';
import { DateRange, DataTypes, NetworkShow } from '@/types';
import { apiDataToCSV } from './dataToCSV';
import { ExportAdvertisersApiResponse, TopAdvertiser } from '@/types/api/advertisers';
import { Campaign, CampaignCategoryStats, ExportCampaignsApiResponse } from '@/types/api/campaigns';
import { ExportCampaignTypesApiResponse } from '@/types/api/campaign-types';
import { ExportShowsApiResponse } from '@/types/api/shows';
import { LevelOfDetail } from '@/types';

export interface BaseDownloadProps {
  networkName?: string;
  data: {
    advertisers?: ExportAdvertisersApiResponse | TopAdvertiser[];
    campaigns?: ExportCampaignsApiResponse | Campaign[];
    campaign_types?: ExportCampaignTypesApiResponse | Record<string, CampaignCategoryStats>;
    shows?: ExportShowsApiResponse | NetworkShow[];
  };
}

interface EarningsDownloadProps extends BaseDownloadProps {
  pageType?: 'earnings';
  dateRange: DateRange;
  selectedShow?: string;
  breakdown?: LevelOfDetail;
}

interface PayoutsDownloadProps extends BaseDownloadProps {
  pageType: 'payouts';
  year: string;
  month: string;
}

type DownloadProps = EarningsDownloadProps | PayoutsDownloadProps;

export interface FileEntry {
  filename: string;
  blob: Blob;
}

// Decided to download the file instead of respect browser's default behavior.
// This is because we wanted to show errors and warnings to the user.
export const downloadFile = (file: Blob, filename: string) => {
  const url = URL.createObjectURL(file);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.setAttribute('download', filename);
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
};

interface GenerateFilenameProps {
  selectedShow?: string;
  pageType: 'earnings' | 'payouts';
  networkName: string;
  dateRange?: DateRange;
  type: DataTypes;
  fileType: 'csv' | 'zip';
  year?: string;
  month?: string;
  breakdown?: LevelOfDetail;
}
export function generateFilename({
  selectedShow,
  networkName,
  dateRange,
  type,
  fileType,
  pageType,
  year,
  month,
  breakdown,
}: GenerateFilenameProps): string {
  const prefix = fileType === 'csv' ? `${networkName}-${type}` : `${networkName}`;

  if (pageType === 'earnings') {
    if (!dateRange) throw new Error('Date range is required for earnings to generate a CSV file');
    if (selectedShow) {
      return `${prefix}-${pageType}-${dateRange.from}-${dateRange.to}-${selectedShow}.${fileType}`;
    }

    if (breakdown) {
      return `${prefix}-${pageType}-${breakdown}-${dateRange.from}-${dateRange.to}.${fileType}`;
    }
    return `${prefix}-${pageType}-${dateRange.from}-${dateRange.to}.${fileType}`;
  }

  if (pageType === 'payouts') {
    if (!year || !month)
      throw new Error('Year and month are required for payouts to generate a CSV file');
    return `${prefix}-${pageType}-${year}-${month}.${fileType}`;
  }

  throw new Error('Invalid page type');
}

// TODO: remove this once payouts endpoints are updated to return ZIP file directly
export const downloadDataToCSV = async ({
  networkName = '',
  data,
  pageType = 'earnings',
  ...props
}: DownloadProps) => {
  const breakdown = 'breakdown' in props ? props.breakdown : undefined;
  const selectedShow = 'selectedShow' in props ? props.selectedShow : undefined;
  const dateRange = 'dateRange' in props ? props.dateRange : undefined;
  const year = 'year' in props ? props.year : undefined;
  const month = 'month' in props ? props.month : undefined;

  const dataTypes = Object.entries(data)
    .filter(([, value]) => value !== undefined)
    .map(([key]) => key as DataTypes);
  if (!dataTypes.length) return;

  // Downloads a single file as a CSV file
  if (dataTypes.length === 1) {
    const dataToDownload = data[dataTypes[0]];
    if (!dataToDownload) return;

    const blob = apiDataToCSV({ type: dataTypes[0], data: dataToDownload, breakdown });
    const filename = generateFilename({
      selectedShow,
      networkName,
      dateRange,
      type: dataTypes[0],
      fileType: 'csv',
      pageType,
      year,
      month,
      breakdown,
    });
    downloadFile(blob, filename);
  } else {
    // Creates multiple CSV files and zips them
    const zip = new JSZip();
    const files: FileEntry[] = [];

    dataTypes.forEach((type) => {
      const dataToZip = data[type];
      if (!dataToZip) return;

      const blob = apiDataToCSV({ type, data: dataToZip, breakdown });

      const filename = generateFilename({
        selectedShow,
        networkName,
        dateRange,
        type,
        fileType: 'csv',
        pageType,
        year,
        month,
        breakdown,
      });
      files.push({ filename, blob });
    });

    files.forEach(({ filename, blob }) => {
      zip.file(filename, blob);
    });

    const zippedFiles = await zip.generateAsync({ type: 'blob' });
    downloadFile(
      zippedFiles,
      generateFilename({
        selectedShow,
        networkName,
        dateRange,
        type: dataTypes[0],
        fileType: 'zip',
        pageType,
        year,
        month,
        breakdown,
      })
    );
  }
};
